'use client';

import Link from 'next/link';
import { Reveal } from '@/components/products/Reveal';
import { priorWork, provenanceNote } from '@/lib/corporate-content';

/**
 * Track record, with provenance stated up front.
 *
 * The rule this section exists to enforce: the portfolio is real, and it was
 * produced under the DataCode studio name. It is evidence of what the team
 * can build — not a claim that the Canadian corporation delivered it. The
 * note is rendered as body copy at the top, not tucked into a caption.
 *
 * Layout family: a two-column index list with a hairline between rows.
 */
export function PriorWorkSection() {
  return (
    <section
      className="self-styled relative z-20 border-b border-white/[0.07]"
      aria-labelledby="prior-work-heading"
    >
      <div className="content-padding">
        <div className="grid grid-cols-1 gap-10 pb-12 pt-24 md:pt-32 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <Reveal>
            <span className="eyebrow">Track record</span>
            <h2
              id="prior-work-heading"
              className="font-six-caps mt-6 uppercase leading-[0.88] text-white"
              style={{ fontSize: 'clamp(2.75rem, 6.5vw, 6rem)' }}
            >
              What the team has shipped
            </h2>
          </Reveal>
          <Reveal delay={90}>
            <p className="max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg md:leading-relaxed">
              {provenanceNote}
            </p>
          </Reveal>
        </div>

        <ul className="flex flex-col">
          {priorWork.map((item, i) => (
            <li key={item.name}>
              <Reveal delay={i * 50}>
                <Link
                  href={item.href}
                  className="group grid grid-cols-1 gap-3 border-t border-white/10 py-7 transition-colors md:grid-cols-[4rem_minmax(0,14rem)_1fr_auto] md:items-baseline md:gap-8 md:py-9"
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/30">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="text-lg font-light leading-snug text-white transition-colors group-hover:text-[var(--color-accent)] md:text-xl">
                    {item.name}
                  </h3>
                  <p className="max-w-2xl text-sm font-light leading-relaxed text-white/55">
                    {item.summary}
                  </p>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/30 md:text-right">
                    {item.meta}
                  </span>
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>

        <Reveal>
          <div className="mb-24 mt-10 border-t border-white/10 pt-8 md:mb-32">
            <Link
              href="/portfolio"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-white/50 transition-colors hover:text-white"
            >
              All case studies
              <span
                aria-hidden="true"
                className="inline-block transition-transform duration-300 group-hover:translate-x-1"
              >
                &rarr;
              </span>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
